/**
 * Coverage details page that renders the full coverage view with tabs,
 * action buttons (Validate, Report Inaccuracy, Refresh), footer navigation,
 * and the associated modals (EvBv, ValidateEv).
 *
 * This component is the orchestrator: data derivation, refresh side effects,
 * and analytics payload construction are extracted into hooks/helpers in this
 * folder. JSX clusters (header, footer, modals) live in `components/`.
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { openModal } from "risa-oasis-ui_v2";
import ValidationMessageList from "../../../components/validationMessageList/validationMessageList";
import { CoverageDetailsValidationMessages } from "../../../constants/coverageDetailsValidationMessages";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import {
  AuthOnFile,
  CoverageResult,
  Demographics,
  GeneralOrderDetails,
  Payload,
  SingleCoverage,
  Status,
} from "../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../enums/modalId";
import { UserGeneralInfoContextKeys } from "../../../enums/userGeneralInfoContextKeys";
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { closeModal, setOpenedModalId } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { UserStore } from "../../../store/UserGeneralInfoStore";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import { Coverage, type CoverageHandle } from "../components/coverage/coverage";
import { EvCoverageNoDataView } from "../components/evCoverageNoDataView/evCoverageNoDataView";
import { hasAnyManuallyUpdatedCoverage } from "../helpers/coverageManualUpdate";
import { getEvCoverageContinueValidationToastMessage } from "../helpers/evCoverageEditValidation";
import { buildOrderContextOnlyCoverageSnapshot } from "../helpers/mapMedicalPaOrderToCoverageInformation";
import { CoverageDetailsFooter } from "./components/coverageDetailsFooter";
import { CoverageDetailsHeader } from "./components/coverageDetailsHeader";
import { CoverageDetailsModals } from "./components/coverageDetailsModals";
import {
  buildContinueButtonEvent,
  buildGoBackEvent,
  buildRaiseQueryModalOpenedEvent,
  buildRefreshButtonEvent,
  buildReportInaccuracyEvent,
  buildValidateEvClickedEvent,
  CoverageAnalyticsContext,
} from "./helpers/coverageAnalyticsEvents";
import { useCoverageData } from "./hooks/useCoverageData";
import { useCoverageDerivedState } from "./hooks/useCoverageDerivedState";
import { useCoverageRefresh } from "./hooks/useCoverageRefresh";

/** Props for the {@link CoverageDetails} component. */
interface CoverageDetailsProps {
  /** Order ID for the coverage view. */
  id?: string;
  /** Combined coverage and coverage-result data from the endpoint. */
  coverageResponse: {
    coverages: SingleCoverage[];
    coverage_results: CoverageResult[];
  };
  /** Patient demographic information. */
  demographicsData?: Demographics;
  /** Current order status (EV/BV, master auth, etc.). */
  status?: Status;
  /** Order payload with practitioner, regimen, and service date fields. */
  payload?: Payload;
  /** Auth-on-file details for auth status checks. */
  authOnFileDetails?: AuthOnFile;
  /** General order details from Postgres. */
  generalOrderDetails?: GeneralOrderDetails;
  /** Submission record for ICD code and regimen state analytics. */
  submissionData?: Submission;
  /** Callback invoked after a successful coverage refresh. */
  onSuccess?: () => void;
  /** Callback invoked when a coverage refresh fails. */
  onError?: (error: Error) => void;
}

/**
 * Displays coverage details with tabbed insurance information, header actions
 * (Validate, Report Inaccuracy, Refresh), and a footer with Continue / Mark as
 * Completed / Go Back navigation. When coverage was manually updated, the header
 * hides Report Inaccuracy/Refresh and shows a "manually updated" badge by MRN.
 * Manages modal flows for EvBv and ValidateEv.
 *
 * @param props - Coverage response data, demographics, status, and auth details
 * @returns The full coverage details page JSX
 */
export const CoverageDetails = (props: CoverageDetailsProps) => {
  const id = props.id ? props.id : undefined;
  const { coverages, coverage_results } = props.coverageResponse;
  const { status } = props;

  const { coverageInformation } = useCoverageData(
    coverages,
    coverage_results,
    props.demographicsData,
    props.payload,
    props.submissionData,
  );

  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [isCoverageEditing, setIsCoverageEditing] = useState(false);
  const coverageRef = useRef<CoverageHandle>(null);
  // Memoized so the Coverage child's sync effect does not run on every render.
  const notifyCoverageTabIndexChange = useCallback((index: number) => {
    setActiveTabIndex(index);
  }, []);

  const handleCoverageEditingChange = useCallback((isEditing: boolean) => {
    setIsCoverageEditing(isEditing);
  }, []);

  const continueValidationMessages = useMemo(() => {
    const messages: string[] = [];
    if (isCoverageEditing) {
      messages.push(CoverageDetailsValidationMessages.COVERAGE_EDITING);
    }
    return messages;
  }, [isCoverageEditing]);

  const dispatch = useDispatch<AppDispatch>();
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: healthFacilityData } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const org_id = getOrgIdForFetchExternalWorklist();

  const orderContextSnapshot = useMemo(
    () =>
      buildOrderContextOnlyCoverageSnapshot(
        props.demographicsData,
        props.payload,
        props.submissionData,
        healthFacilityData?.TaxID ?? "",
      ),
    [
      healthFacilityData?.TaxID,
      props.demographicsData,
      props.payload,
      props.submissionData,
    ],
  );
  // In the no-coverage fallback (EvCoverageNoDataView) the demographics
  // sidebar can only be populated from order-level context: demographics
  // (DOB/sex), the date of service, or the requesting practitioner. When none
  // of those are present — e.g. a bare cross-org order with no demographics
  // yet — rendering the sidebar would be an empty, meaningless column, so
  // `hideSidebar` hides it and the empty state takes the full width.
  const hasOrderContextForSidebar = Boolean(
    props.demographicsData?.date_of_birth ||
    props.demographicsData?.sex ||
    props.payload?.date_of_service ||
    props.submissionData?.requesting_practitioner_details?.first_name ||
    props.submissionData?.requesting_practitioner_details?.last_name ||
    props.submissionData?.requesting_practitioner_details?.npi,
  );

  const { isRefreshingCoverage, refreshCoverage } = useCoverageRefresh(
    id,
    props.onSuccess,
    props.onError,
  );

  const {
    orderedCoverages,
    activeCoverageResultForValidateEv,
    serviceTypeCodeLabelMap,
    submitButtonText,
    shouldOpenHappyModal,
    isPrimaryInsuranceInActive,
  } = useCoverageDerivedState({
    coverages,
    coverage_results,
    coverageInformation,
    status,
    activeTabIndex,
  });

  // Reopen modals after Redux flips `openedModalId`. Kept inline because it
  // depends on the imperative `openModal` UI side effect.
  useEffect(() => {
    if (openedModalId === ModalId.INSURANCE_ERROR_DIALOG) {
      openModal(ModalId.INSURANCE_ERROR_DIALOG);
    }
    if (openedModalId === ModalId.EVBV_MODAL) {
      openModal(ModalId.EVBV_MODAL);
    }
  }, [openedModalId]);

  useEffect(() => {
    if (showValidateModal) {
      openModal("validate-ev-modal");
    }
  }, [showValidateModal]);

  const navigate = useNavigate();

  const analyticsContext: CoverageAnalyticsContext = {
    demographics: props.demographicsData,
    orderId: id ?? "",
    orgId: org_id ?? "",
    userId: user?.id ?? "",
    userEmail: user?.email ?? "",
    payerName,
  };

  /**
   * Handles the footer submit button click. Fires appropriate analytics events
   * and either opens a modal (HappyFlow or CommentRpa) for inactive insurance,
   * or navigates to the single order view for active insurance.
   */
  const handleSubmitClick = () => {
    if (isPrimaryInsuranceInActive) {
      apiWorkerManager.enqueueAndProcess(
        buildRaiseQueryModalOpenedEvent(analyticsContext),
      );
      if (shouldOpenHappyModal) {
        dispatch(setOpenedModalId({ id: ModalId.HAPPY_FLOW_MODAL }));
      } else {
        dispatch(setOpenedModalId({ id: ModalId.COMMENT_RPA_MODAL_NEW }));
      }
      return;
    }

    const continueValidation = coverageRef.current?.validateForContinue();
    if (continueValidation && !continueValidation.valid) {
      toast.error(
        getEvCoverageContinueValidationToastMessage(continueValidation.issues),
      );
      return;
    }

    apiWorkerManager.enqueueAndProcess(
      buildContinueButtonEvent(analyticsContext),
    );
    navigate(`/worklists/singleOrder/${id}`);
  };

  const handleReportInaccuracyClick = () => {
    dispatch(setOpenedModalId({ id: ModalId.EVBV_MODAL }));
    apiWorkerManager.enqueueAndProcess(
      buildReportInaccuracyEvent(analyticsContext),
    );
  };

  const handleValidateClick = () => {
    setShowValidateModal(true);
    apiWorkerManager.enqueueAndProcess(
      buildValidateEvClickedEvent(analyticsContext),
    );
  };

  const handleRefreshClick = () => {
    refreshCoverage();
    apiWorkerManager.enqueueAndProcess(
      buildRefreshButtonEvent(analyticsContext),
    );
  };

  const handleGoBackClick = () => {
    const parentTable =
      UserStore.getItem(UserGeneralInfoContextKeys.PARENT_TABLE) ?? "";
    navigate(`/worklists/${parentTable}`);
    apiWorkerManager.enqueueAndProcess(buildGoBackEvent(analyticsContext));
  };

  const patientFullName = generateFullName(
    props?.demographicsData?.first_name ?? "",
    "",
    props?.demographicsData?.last_name ?? "",
  );
  const patientIdentifier =
    props?.demographicsData?.patient_mrn ??
    props?.demographicsData?.patient_id ??
    "";
  const isManuallyUpdated = hasAnyManuallyUpdatedCoverage(coverages);

  return (
    <>
      <div className="coverage-details--container flex h-full w-full flex-col overflow-hidden">
        <div className="coverage-details--header-with-body flex flex-1 flex-col gap-1 overflow-hidden px-1.5 py-1 sm:gap-1.5 sm:px-2.5 sm:py-1.5 md:gap-0.5 md:px-3 md:pb-0.5 md:pt-2 lg:px-4">
          <CoverageDetailsHeader
            patientFullName={patientFullName}
            patientIdentifier={patientIdentifier}
            isManuallyUpdated={isManuallyUpdated}
            isRefreshingCoverage={isRefreshingCoverage}
            onReportInaccuracyClick={handleReportInaccuracyClick}
            onValidateClick={handleValidateClick}
            onRefreshClick={handleRefreshClick}
          />
          <div className="coverage-details--body flex-1 overflow-hidden">
            {coverageInformation ? (
              <Coverage
                ref={coverageRef}
                coverageInformation={coverageInformation}
                currentTabIndex={notifyCoverageTabIndexChange}
                demographicsData={props?.demographicsData ?? {}}
                authOnFileDetails={props?.authOnFileDetails ?? {}}
                submissionData={props?.submissionData ?? ({} as Submission)}
                status={props?.status ?? {}}
                coverage={orderedCoverages}
                orderId={id ?? ""}
                orgId={getOrgIdForFetchExternalWorklist()}
                onCoverageSaved={props.onSuccess}
                generalOrderDetails={props?.generalOrderDetails}
                serviceTypeCodeLabelMap={serviceTypeCodeLabelMap}
                onEditingChange={handleCoverageEditingChange}
                isManuallyUpdated={isManuallyUpdated}
              />
            ) : (
              <EvCoverageNoDataView
                orderContextSnapshot={orderContextSnapshot}
                hideSidebar={!hasOrderContextForSidebar}
                onRefreshClick={
                  isManuallyUpdated ? undefined : handleRefreshClick
                }
                isRefreshing={isRefreshingCoverage}
              />
            )}
          </div>
        </div>
        <CoverageDetailsFooter
          submitButtonText={submitButtonText}
          isContinueDisabled={isCoverageEditing}
          tooltipContent={
            isCoverageEditing ? (
              <ValidationMessageList messages={continueValidationMessages} />
            ) : undefined
          }
          onCancelClick={handleGoBackClick}
          onSubmitClick={handleSubmitClick}
        />
        <CoverageDetailsModals
          openedModalId={openedModalId}
          showValidateModal={showValidateModal}
          onCloseEvBvModal={() => dispatch(closeModal())}
          onCloseValidateModal={() => setShowValidateModal(false)}
          orderId={id ?? ""}
          activeTabIndex={activeTabIndex}
          demographicsData={props?.demographicsData}
          orderedCoverages={orderedCoverages}
          coverageResults={coverage_results ?? []}
          activeCoverageResultForValidateEv={activeCoverageResultForValidateEv}
          coverageResponse={props.coverageResponse}
        />
      </div>
    </>
  );
};
